import { type FC, useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";

import MainLayout from "@/pages/layouts/MainLayout";
import { PageActions } from "@/pages/layouts/components/PageActions";
import { Button } from "@/shared/components/ui/button";
import { CalendlyModal } from "@/shared/components/overlay/CalendlyModal";
import PostLossPaywall from "@/pages/postloss/components/PostLossPaywall";
import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { useIntakeStatus } from "@/shared/hooks/useIntakeStatus";
import { useKeyDocCount } from "@/shared/hooks/useKeyDocCount";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useAttorneyHelplineEnabled } from "@/shared/hooks/useAttorneyHelplineEnabled";
import { useHasAuthorityDocument } from "@/shared/hooks/useHasAuthorityDocument";
import { useModuleProgress } from "@/shared/hooks/useModuleProgress";
import { ASSET_SEARCH_TASK_ID } from "@/shared/constants/assetSearch";
import { useAuthorityContext } from "@/shared/hooks/useAuthorityContext";
import { useProbateTaskAnswers } from "@/shared/hooks/useProbateTaskAnswers";
import { useUserCustomizations } from "@/shared/hooks/useUserCustomizations";
import { hasMinTier } from "@/shared/utils/tierOrder";
import { isProfessionalContact } from "@/shared/utils/contactUtils";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { notifyGetAuthorizedStarted } from "@/shared/utils/slackNotifications";
import { CALENDLY_URLS, ESTATE_EXPERT_BOOKING_SOURCE } from "@/shared/constants/calendlyUrls";
import { useSaveCalendlyBooking } from "@/shared/hooks/useSaveCalendlyBooking";
import { getStateName } from "@/pages/postloss/estate-essentials/data/stateContent";

import { AdminRoadmap } from "./components/AdminRoadmap";
import { ProbateRoadmap } from "./components/ProbateRoadmap";
import { GenericAuthorityGuide } from "./components/GenericAuthorityGuide";

import guideImg from "@/shared/assets/illustrations/guide-in-progress.svg";

const notifiedEstates = new Set<string>();

export const GetAuthorizedPage: FC = () => {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const decedentFirstName = person?.first_name ?? "the decedent";
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [calendlyOpen, setCalendlyOpen] = useState(false);

  const { hasCompletedLegalAuthority: hasCompletedAuthority, isFetched } = useIntakeStatus();
  const { tier, isLoading: isTierLoading } = usePaymentTier(estateId);
  const { keyDocCount, keyDocTotal } = useKeyDocCount(estateId);
  const { contacts } = useUserContacts();
  const { path, hasWill, isNY, state: resolvedState, county, countyLabel, authorityDocumentName, isReady } = useAuthorityContext(estateId);
  const { isLoading: isTasksLoading } = useProbateTaskAnswers(estateId);

  const { getCustomization, setCustomization, isFetched: isCustomizationsFetched } = useUserCustomizations();

  const helplineEnabled = useAttorneyHelplineEnabled();
  const { hasAuthorityDocument } = useHasAuthorityDocument(estateId);
  const { taskStatusMap } = useModuleProgress();
  const assetSearchStatus = taskStatusMap.get(ASSET_SEARCH_TASK_ID);
  const isPaid = hasMinTier(tier, "basic");
  const { mutate: saveCalendlyBooking } = useSaveCalendlyBooking(estateId, ESTATE_EXPERT_BOOKING_SOURCE);

  const handleEventScheduled = useCallback(
    (eventUri: string, inviteeUri: string) => {
      saveCalendlyBooking({ eventUri, inviteeUri });
    },
    [saveCalendlyBooking],
  );

  const hasSeenAuthorityPage = getCustomization<boolean>("has_seen_authority_page", false);
  const [localDismissed, setLocalDismissed] = useState(false);
  const isFirstVisit = isPaid && !hasSeenAuthorityPage && !localDismissed;

  const dismissFirstVisit = useCallback(() => {
    if (!hasSeenAuthorityPage && !localDismissed) {
      setLocalDismissed(true);
      setCustomization("has_seen_authority_page", true).catch(() => {});
    }
  }, [hasSeenAuthorityPage, localDismissed, setCustomization]);

  const userId = user?.user_id;

  useEffect(() => {
    const notifyKey = `${userId}:${estateId}`;
    if (!isPaid || !userId || !hasCompletedAuthority || notifiedEstates.has(notifyKey) || !estateId || !isFetched || !isReady || isTierLoading || isTasksLoading || !isCustomizationsFetched) return;
    if (getCustomization<boolean>(`has_notified_get_authorized_${estateId}`, false)) return;
    notifiedEstates.add(notifyKey);
    notifyGetAuthorizedStarted({ estateId, authorityPath: path, county }).then((sent) => {
      if (sent) {
        setCustomization(`has_notified_get_authorized_${estateId}`, true).catch(() => {});
      } else {
        notifiedEstates.delete(notifyKey);
      }
    }).catch(() => {
      notifiedEstates.delete(notifyKey);
    });
  }, [isPaid, userId, hasCompletedAuthority, estateId, isFetched, isReady, isTierLoading, isTasksLoading, isCustomizationsFetched, path, county, setCustomization, getCustomization]);

  const showGenericGuide = isReady && !isNY && resolvedState !== "FL";
  const { data: hasEstateAccount = false } = useQuery({
    queryKey: ["hasEstateAccount", estateId],
    queryFn: async () => {
      if (!estateId) return false;
      const { count, error } = await supabase
        .from("plaid_connections")
        .select("id", { count: "exact", head: true })
        .eq("estate_id", estateId)
        .eq("purpose", "estate_account")
        .neq("status", "disconnected");
      if (error) throw error;
      return (count ?? 0) > 0;
    },
    enabled: !!estateId && showGenericGuide,
  });

  const { data: propertyCount = 0 } = useQuery({
    queryKey: ["propertyCount", estateId],
    queryFn: async () => {
      if (!estateId) return 0;
      const { count } = await supabase
        .from("estate_items")
        .select("id", { count: "exact", head: true })
        .eq("estate_id", estateId)
        .eq("category", "Properties")
        .eq("is_hidden", false);
      return count ?? 0;
    },
    enabled: !!estateId,
  });

  if (!isFetched || !estateId || !isReady || isTierLoading || isTasksLoading || !isCustomizationsFetched) {
    return (
      <MainLayout header="Get authorized" actions={<PageActions />}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="size-8 animate-spin text-brand" />
        </div>
      </MainLayout>
    );
  }

  if (!hasCompletedAuthority) {
    return <Navigate to={POST_LOSS_ROUTES.OVERVIEW} replace />;
  }

  const familyCount = contacts.filter((c) => !isProfessionalContact(c)).length;
  const attorneyContact = contacts.find(
    (c) =>
      c.relationship === "attorney" ||
      c.professional_role === "estate-attorney" ||
      c.professional_role === "other-attorney",
  );
  const attorneyName = attorneyContact
    ? [attorneyContact.first_name, attorneyContact.last_name].filter(Boolean).join(" ")
    : null;
  const willStatus = hasWill === "yes" ? "Added" : hasWill === "no" ? "No will" : null;

  const isFL = resolvedState === "FL";

  if (!isNY && !isFL) {
    return (
      <MainLayout header="Get authorized" actions={<PageActions />}>
        <GenericAuthorityGuide
          stateName={getStateName(resolvedState ?? "") || "your state"}
          countyLabel={countyLabel || "your county"}
          decedentFirstName={decedentFirstName}
          authorityDocumentName={authorityDocumentName}
          authorityPath={path}
          isPaidUser={isPaid}
          tier={tier}
          estateId={estateId}
          onOpenPaywall={() => setPaywallOpen(true)}
          onOpenCalendly={() => setCalendlyOpen(true)}
          setCalendlyOpen={setCalendlyOpen}
          familyCount={familyCount}
          propertyCount={propertyCount}
          willStatus={willStatus}
          keyDocCount={keyDocCount}
          keyDocTotal={keyDocTotal}
          attorneyName={attorneyName}
          stateCode={resolvedState}
          hasEstateAccount={hasEstateAccount}
          hasAuthorityDocument={hasAuthorityDocument}
          assetSearchStatus={assetSearchStatus}
          hasWill={hasWill}
        />
        <CalendlyModal
          isOpen={calendlyOpen}
          onClose={() => setCalendlyOpen(false)}
          url={CALENDLY_URLS.ESTATE_SETTLEMENT_QUOTE}
          onEventScheduled={handleEventScheduled}
        />
        <PostLossPaywall
          isOpen={paywallOpen}
          onOpenChange={setPaywallOpen}
          promptLocation="get_authorized"
          currentTier={tier}
          returnPath={POST_LOSS_ROUTES.GET_AUTHORIZED}
        />
      </MainLayout>
    );
  }

  if (isNY && path === "administration") {
    return (
      <AdminRoadmap
        estateId={estateId}
        decedentFirstName={decedentFirstName}
        county={county}
        countyLabel={countyLabel}
        isPaid={isPaid}
        tier={tier}
        helplineEnabled={helplineEnabled}
        isFirstVisit={isFirstVisit}
        onDismissFirstVisit={dismissFirstVisit}
        familyCount={familyCount}
        propertyCount={propertyCount}
        keyDocCount={keyDocCount}
        keyDocTotal={keyDocTotal}
        attorneyName={attorneyName}
      />
    );
  }

  if (isNY && path === "probate") {
    return (
      <ProbateRoadmap
        estateId={estateId}
        decedentFirstName={decedentFirstName}
        county={county}
        countyLabel={countyLabel}
        isPaid={isPaid}
        tier={tier}
        helplineEnabled={helplineEnabled}
        isFirstVisit={isFirstVisit}
        onDismissFirstVisit={dismissFirstVisit}
        familyCount={familyCount}
        propertyCount={propertyCount}
        keyDocCount={keyDocCount}
        keyDocTotal={keyDocTotal}
        attorneyName={attorneyName}
      />
    );
  }

  return (
    <MainLayout header="Get authorized" actions={<PageActions />} className="flex flex-col">
      <div className="flex flex-1 flex-col items-center justify-center text-center gap-6 px-4 py-8">
        <img
          src={guideImg}
          alt="Personalized guide in progress"
          className="w-[182px] h-[170px] object-contain"
        />
        <div className="flex flex-col gap-2 max-w-[500px]">
          <h1>Your personalized guide is in progress</h1>
          <p className="text-secondary">
            We&apos;re preparing the next steps to help you get authorized. Our team will reach out
            shortly with your custom guide.
          </p>
        </div>
        <Button variant="brand" size="lg" rounded onClick={() => window.Intercom?.("show")}>
          Contact us
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </MainLayout>
  );
};
